import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, AppBar, Toolbar, Typography } from '@mui/material';
import useStudioStore from '../state/studioStore';
import WorkspaceDashboard from './WorkspaceDashboard';
import GlobalAlerts from './GlobalAlerts';

const NAV_HEIGHT = 64;
const KEYBOARD_TOOL_ORDER = ['live', 'diff', 'slices', 'studio', 'gate'];

export default function AppShell() {
  const location = useLocation();
  const initializeFromURL = useStudioStore((state) => state.initializeFromURL);
  const activeTool = useStudioStore((state) => state.activeTool);
  const navigateToTab = useStudioStore((state) => state.navigateToTab);

  useEffect(() => {
    initializeFromURL();
  }, [initializeFromURL]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === 'Tab') {
        event.preventDefault();
        const currentIndex = Math.max(0, KEYBOARD_TOOL_ORDER.indexOf(activeTool || 'live'));
        const direction = event.shiftKey ? -1 : 1;
        const nextIndex = (currentIndex + direction + KEYBOARD_TOOL_ORDER.length) % KEYBOARD_TOOL_ORDER.length;
        navigateToTab(KEYBOARD_TOOL_ORDER[nextIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTool, navigateToTab]);

  // Handle legacy query params when hitting root directly
  useEffect(() => {
    if (location.pathname === '/') {
      const params = new URLSearchParams(location.search);
      const legacyTool = params.get('tool') || params.get('tab');
      if (legacyTool) {
        navigateToTab(legacyTool);
      }
    }
  }, [location, navigateToTab]);

  return (
    <Box
      sx={{
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #292956ff 0%, #2f2663ff 25%, #2c386cff 50%, #32315aff 100%, #2b2c57ff 100%)'
      }}
    >
      <GlobalAlerts />
      <AppBar
        color="transparent"
        elevation={0}
        position="sticky"
        sx={{
          height: NAV_HEIGHT,
          justifyContent: 'center',
          backdropFilter: 'blur(20px)',
          backgroundColor: (theme) => `${theme.palette.background.paper}15`,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}30`,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: NAV_HEIGHT }}>
          <Typography variant="h6" fontWeight={600}>
            Studio Workspace
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
            Iterative prompt development & evaluation
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          height: `calc(100vh - ${NAV_HEIGHT}px)`,
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <Routes>
          <Route path="/workspace" element={<WorkspaceDashboard />} />

          {/* Legacy direct routes redirect into workspace */}
          <Route path="/flow" element={<Navigate to="/workspace?tool=flow" replace />} />
          <Route path="/live" element={<Navigate to="/workspace?tool=live" replace />} />
          <Route path="/live-trial" element={<Navigate to="/workspace?tool=live" replace />} />
          <Route path="/studio" element={<Navigate to="/workspace?tool=studio" replace />} />
          <Route path="/rag-studio" element={<Navigate to="/workspace?tool=studio" replace />} />
          <Route path="/diff" element={<Navigate to="/workspace?tool=diff" replace />} />
          <Route path="/slices" element={<Navigate to="/workspace?tool=slices" replace />} />
          <Route path="/slice" element={<Navigate to="/workspace?tool=slices" replace />} />
          <Route path="/slice-hunter" element={<Navigate to="/workspace?tool=slices" replace />} />
          <Route path="/sliceHunter" element={<Navigate to="/workspace?tool=slices" replace />} />
          <Route path="/gate" element={<Navigate to="/workspace?tool=gate" replace />} />

          {/* Root fallback */}
          <Route path="/" element={<Navigate to="/workspace" replace />} />
        </Routes>
      </Box>
    </Box>
  );
}
